import { useState, useEffect, useCallback } from "react";
import { ContractorApi } from "api/contractors/service";
import { UpgradeWorkOrderDialog } from "./UpgradeWorkOrderDialog";
import {
  ArrowLeft,
  Download,
  ArrowUpRight,
  Calendar,
  FileText,
  IndianRupee,
  Building2,
  User,
  Clock,
  CheckCircle2,
  AlertCircle,
  Shield,
  ChevronDown,
  ChevronUp,
  Paperclip,
  History,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { PDFPreviewModal } from "./PDFPreviewModal";

// Detailed work order details will be fetched from API

export function WorkOrderDetailView({
  workOrder,
  contractor,
  onBack,
  onUpgrade,
}) {
  const [expandedVersions, setExpandedVersions] = useState([]);
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [organization, setOrganization] = useState(null);

  useEffect(() => {
    try {
      const org = localStorage.getItem("organization");
      if (org) {
        setOrganization(JSON.parse(org));
      }
    } catch (err) {
      console.error("Error parsing organization from localStorage:", err);
    }
  }, []);

  const fetchDetails = useCallback(async () => {
    if (!workOrder?.workorder_id) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await ContractorApi.getWorkOrderDetail(workOrder.workorder_id);

      if (response.status) {
        const apiData = response.data;
        const versionCount = apiData.version_timeline?.length || 0;
        const startDate = apiData.version_timeline?.[versionCount - 1]?.start_date;
        const endDate = apiData.version_timeline?.[0]?.end_date;
        const duration = differenceInDays(new Date(endDate), new Date(startDate));
        // Map API data to the UI structure
        const mappedDetails = {
          ...workOrder,
          id: apiData.id,
          status: apiData.status,
          projectName: apiData.site_name,
          contractType: apiData.contract_type,
          paymentStructure: apiData.payment_structure,
          startDate,
          endDate,
          duration,
          value: apiData.financial_summary?.total_value || 0,
          paidAmount: apiData.financial_summary?.total_paid || 0,
          approvalStatus: apiData.status,
          scopeItems: (apiData.scope_of_work || []).map(item => ({
            id: item.id,
            item: item.item_name,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitCost: item.unit_cost,
            totalCost: item.total_price
          })),
          milestones: (apiData.milestones || []).map(m => ({
            id: m.id,
            name: m.name,
            description: m.description,
            percentage: (m.amount / apiData.financial_summary?.total_value) * 100,
            value: m.amount,
            status: m.paid_so_far >= m.amount ? "Paid" : (m.paid_so_far > 0 ? "In Progress" : "Pending")
          })),
          monthlyBills: apiData.monthly_bills,
          versions: (apiData.version_timeline || []).map(v => ({
            version: v.version_number,
            updatedOn: v.created_on,
            totalValue: v.total_value,
            updatedBy: v.updated_by,
            scopeItems: (v.scope_items || []).map(item => ({
              id: item.id,
              item: item.item_name,
              description: item.description,
              totalCost: item.total_price
            })),
            milestones: (v.milestones || []).map(m => ({
              id: m.id,
              name: m.name,
              description: m.description,
              percentage: (m.amount / v.total_value) * 100,
            })),
            terms: v.terms_and_conditions || "",
            acknowledgmentDate: v.created_on, // Fallback as API doesn't have specific acknowledgment_date
          })),
          terms: apiData.version_timeline?.[0]?.terms_and_conditions || "Standard contract terms apply.",
          acknowledgmentDate: apiData.date_of_issue,
          attachments: [], // API doesn't provide attachments yet
          paymentTerms: apiData.version_timeline?.[0]?.payment_terms || "",
          retentionPercent: 0, // Not in API
          scopeNotes: "" // Not in API
        };

        setDetails(mappedDetails);
      } else {
        setError(response.message || "Failed to fetch work order details");
      }
    } catch (err) {
      console.error("Error fetching work order details:", err);
      setError("An error occurred while fetching details");
    } finally {
      setIsLoading(false);
    }
  }, [workOrder?.workorder_id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount.toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    return format(new Date(dateStr), "dd MMM yyyy");
  };

  const duration = differenceInDays(new Date(workOrder.endDate), new Date(workOrder.startDate));

  const toggleVersion = (version) => {
    setExpandedVersions(prev =>
      prev.includes(version)
        ? prev.filter(v => v !== version)
        : [...prev, version]
    );
  };

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-500/10 text-green-600 border-green-500/30";
      case "completed":
        return "bg-sm-secondary/10 text-sm-secondary border-sm-secondary/30";
      case "paid":
        return "bg-green-500/10 text-green-600 border-green-500/30";
      case "pending":
        return "bg-sm-accent/10 text-sm-accent border-sm-accent/30";
      case "in progress":
        return "bg-gray-500/10 text-gray-600 border-gray-500/30";
      case "draft":
        return "bg-gray-100 text-gray-600 border-gray-200";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  const handleDownload = () => {
    setShowPreviewModal(true);
  };

  const handleUpgradeSuccess = () => {
    // Refresh page as requested
    fetchDetails();
  };

  // Reusable Components replacements
  const Button = ({ variant = "default", size = "default", className = "", children, ...props }) => {
    const baseStyles = "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";
    const variants = {
      default: "bg-sm-primary text-white hover:bg-sm-primary/90",
      destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
      outline: "border border-input bg-background hover:bg-sm-accent hover:text-sm-accent-foreground",
      secondary: "bg-sm-secondary text-sm-secondary-foreground hover:bg-sm-secondary/80",
      ghost: "hover:bg-sm-accent hover:text-sm-accent-foreground",
      link: "text-sm-primary underline-offset-4 hover:underline",
    };
    const sizes = {
      default: "h-10 px-4 py-2",
      sm: "h-9 rounded-md px-3",
      lg: "h-11 rounded-md px-8",
      icon: "h-10 w-10",
    };

    // Custom handling for size="icon" combined with h-9 w-9 overrides in className
    const sizeClasses = sizes[size];

    return (
      <button className={`${baseStyles} ${variants[variant]} ${sizeClasses} ${className}`} {...props}>
        {children}
      </button>
    );
  };

  const Badge = ({ variant = "none", className = "", children }) => {
    const baseStyles = "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
    const variants = {
      none: "",
      default: "border-transparent bg-sm-primary text-sm-primary-foreground hover:bg-sm-primary/80",
      secondary: "border-transparent bg-sm-secondary text-sm-secondary-foreground hover:bg-sm-secondary/80",
      destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
      outline: "text-foreground",
    };
    return (
      <div className={`${baseStyles} ${variants[variant]} ${className}`}>
        {children}
      </div>
    );
  };

  const Card = ({ className = "", children }) => (
    <div className={`rounded-lg border bg-white text-card-foreground shadow-sm ${className}`}>
      {children}
    </div>
  );

  const CardHeader = ({ className = "", children }) => (
    <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>{children}</div>
  );

  const CardTitle = ({ className = "", children }) => (
    <h3 className={`text-2xl font-semibold leading-none tracking-tight ${className}`}>{children}</h3>
  );

  const CardContent = ({ className = "", children }) => (
    <div className={`p-6 ${className}`}>{children}</div>
  );

  const Separator = ({ className = "" }) => (
    <div className={`shrink-0 bg-border h-[1px] w-full ${className}`} />
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] w-full bg-background rounded-xl border border-border">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sm-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] w-full bg-background rounded-xl border border-border p-6 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-destructive font-semibold text-lg mb-2">Error</p>
        <p className="text-sm-secondary-foreground mb-4">{error}</p>
        <Button onClick={fetchDetails}>Try Again</Button>
      </div>
    );
  }

  if (!details) return null;

  const grandTotal = details.scopeItems.reduce((sum, item) => sum + item.totalCost, 0);
  const currentVersion = details.versions.length ? Math.max(...details.versions.map(v => v.version)) : 1;

  return (
    <div className="space-y-6 p-4 pb-8">
      {/* Header Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-9 w-9"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-sm-primary">#{workOrder.id}</h1>
              <Badge className={`text-xs ${getStatusBadgeClass(details.approvalStatus)} border uppercase`}>
                {details.approvalStatus}
              </Badge>
              <Badge variant="outline" className="text-xs font-normal border-gray-300">
                v{currentVersion}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {contractor.name} – {details.projectName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="h-9 text-sm gap-2"
            onClick={handleDownload}
          >
            <Download className="h-4 w-4" />
            Download Work Order
          </Button>
          <Button
            className="h-9 text-sm gap-2 bg-sm-primary hover:bg-sm-primary/90"
            onClick={() => setShowUpgradeDialog(true)}
          >
            <ArrowUpRight className="h-4 w-4" />
            Upgrade Contract
          </Button>
        </div>
      </div>

      <UpgradeWorkOrderDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        workOrder={details}
        onUpgradeSuccess={handleUpgradeSuccess}
      />

      <PDFPreviewModal
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        details={details}
        contractor={contractor}
        organization={organization}
      />

      {/* Section 1: Work Order Overview */}
      <Card className="border-border/60 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Work Order Overview</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <User className="h-3.5 w-3.5" />
                Contractor
              </div>
              <p className="text-sm font-medium">{contractor.name}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Building2 className="h-3.5 w-3.5" />
                Project
              </div>
              <p className="text-sm font-medium">{details.projectName}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <FileText className="h-3.5 w-3.5" />
                Contract Type
              </div>
              <p className="text-sm font-medium capitalize">{details.contractType?.toLowerCase()?.replace('_', ' ')}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <IndianRupee className="h-3.5 w-3.5" />
                Payment Structure
              </div>
              <Badge className="text-xs font-normal bg-sm-secondary/10 text-sm-secondary border-0 capitalize">
                {workOrder?.paymentStructure}
              </Badge>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <IndianRupee className="h-3.5 w-3.5" />
                Work Order Value
              </div>
              <p className="text-sm font-semibold text-sm-primary">{formatCurrency(details.value)}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                Amount Paid
              </div>
              <p className="text-sm font-semibold text-green-600">{formatCurrency(details.paidAmount)}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <AlertCircle className="h-3.5 w-3.5 text-yellow-600" />
                Due Amount
              </div>
              <p className="text-sm font-semibold text-yellow-600">{formatCurrency(details.value - details.paidAmount)}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                Duration
              </div>
              <p className="text-sm font-medium">{details.duration} Days</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                Start Date
              </div>
              <p className="text-sm font-medium">{details.startDate ? formatDate(details.startDate) : '-'}</p>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                End Date
              </div>
              <p className="text-sm font-medium">{details.endDate ? formatDate(details.endDate) : '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Scope & Items */}
      <Card className="border-border/60 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Scope & Items</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          <div className="rounded-lg border border-border/60 overflow-hidden">
            <table className="w-full caption-bottom text-sm">
              <thead className="[&_tr]:border-b">
                <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted hover:bg-transparent bg-muted/30">
                  <th className="h-9 px-4 text-left align-middle font-medium text-muted-foreground text-xs">Item</th>
                  <th className="h-9 px-4 text-left align-middle font-medium text-muted-foreground text-xs">Description</th>
                  <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-right">Quantity</th>
                  <th className="h-9 px-4 text-left align-middle font-medium text-muted-foreground text-xs">Unit</th>
                  <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-right">Unit Cost</th>
                  <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-right">Total</th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {details.scopeItems.map((item, index) => (
                  <tr
                    key={item.id}
                    className={`border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted ${index % 2 === 0 ? "bg-background" : "bg-muted/20"}`}
                  >
                    <td className="p-4 align-middle py-2.5 text-sm font-medium">{item.item}</td>
                    <td className="p-4 align-middle py-2.5 text-sm text-muted-foreground">{item.description}</td>
                    <td className="p-4 align-middle py-2.5 text-sm text-right">{item.quantity.toLocaleString()}</td>
                    <td className="p-4 align-middle py-2.5 text-sm">{item.unit}</td>
                    <td className="p-4 align-middle py-2.5 text-sm text-right">₹{item.unitCost.toLocaleString()}</td>
                    <td className="p-4 align-middle py-2.5 text-sm text-right font-medium">{formatCurrency(item.totalCost)}</td>
                  </tr>
                ))}
                <tr className="border-b transition-colors data-[state=selected]:bg-muted bg-muted/40 hover:bg-muted/40">
                  <td colSpan={5} className="p-4 align-middle py-2.5 text-sm font-semibold text-right">
                    Grand Total:
                  </td>
                  <td className="p-4 align-middle py-2.5 text-sm text-right font-bold text-sm-primary">
                    {formatCurrency(grandTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {details.scopeNotes && (
            <div className="bg-muted/30 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Scope Notes:</p>
              <p className="text-sm">{details.scopeNotes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 3: Payment Structure */}
      <Card className="border-border/60 shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium">Payment Structure</CardTitle>
            <Badge variant="secondary" className="text-xs bg-sm-secondary/10 text-sm-secondary border-0 capitalize">
              {details.paymentStructure?.toLowerCase()?.replace('_', ' ')}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Milestone-Based */}
          {details.paymentStructure === "MILESTONE_BASED" && details.milestones && (
            <div className="rounded-lg border border-border/60 overflow-hidden">
              <table className="w-full caption-bottom text-sm">
                <thead className="[&_tr]:border-b">
                  <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted hover:bg-transparent bg-muted/30">
                    <th className="h-9 px-4 text-left align-middle font-medium text-muted-foreground text-xs">Milestone</th>
                    <th className="h-9 px-4 text-left align-middle font-medium text-muted-foreground text-xs">Description</th>
                    <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-center">%</th>
                    <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-right">Value</th>
                    <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="[&_tr:last-child]:border-0">
                  {details.milestones.map((milestone) => (
                    <tr key={milestone.id} className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                      <td className="p-4 align-middle py-2.5 text-sm font-medium">{milestone.name}</td>
                      <td className="p-4 align-middle py-2.5 text-sm text-muted-foreground">{milestone.description}</td>
                      <td className="p-4 align-middle py-2.5 text-sm text-center">{milestone.percentage.toFixed(1)}%</td>
                      <td className="p-4 align-middle py-2.5 text-sm text-right font-medium">{formatCurrency(milestone.value)}</td>
                      <td className="p-4 align-middle py-2.5 text-center">
                        <Badge className={`text-xs ${getStatusBadgeClass(milestone.status)} border capitalize`}>
                          {milestone.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Monthly Running Bill (Mock UI remains if structure is Monthly, though API has Lumpsum/Milestone) */}
          {details.paymentStructure === "MONTHLY_RUNNING_BILL" && details.monthlyBills && (
            <div className="rounded-lg border border-border/60 overflow-hidden">
              <table className="w-full caption-bottom text-sm">
                <thead className="[&_tr]:border-b">
                  <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted hover:bg-transparent bg-muted/30">
                    <th className="h-9 px-4 text-left align-middle font-medium text-muted-foreground text-xs">Month</th>
                    <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-center">Progress</th>
                    <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-right">Amount</th>
                    <th className="h-9 px-4 align-middle font-medium text-muted-foreground text-xs text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="[&_tr:last-child]:border-0">
                  {details.monthlyBills.map((bill, index) => (
                    <tr key={index} className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                      <td className="p-4 align-middle py-2.5 text-sm font-medium">{bill.month}</td>
                      <td className="p-4 align-middle py-2.5 text-sm text-center">{bill.progress}%</td>
                      <td className="p-4 align-middle py-2.5 text-sm text-right font-medium">{formatCurrency(bill.amount)}</td>
                      <td className="p-4 align-middle py-2.5 text-center">
                        <Badge className={`text-xs ${getStatusBadgeClass(bill.status)} border`}>
                          {bill.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Lumpsum */}
          {details.paymentStructure === "LUMPSUM_CUSTOM" && (
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Total Contract Value</p>
                <p className="text-lg font-semibold text-sm-primary">{formatCurrency(details.value)}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Payment Terms</p>
                <pre className="text-sm whitespace-pre-wrap font-outfit">{details.paymentTerms || '-'}</pre>
              </div>
              {details.retentionPercent > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">Retention</p>
                  <p className="text-sm font-medium">{details.retentionPercent}%</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 4: Terms, Acknowledgment, and Documents */}
      <Card className="border-border/60 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium">Terms & Documents</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground font-medium">Terms & Conditions</p>
            <div className="bg-muted/30 rounded-lg p-3">
              <pre className="text-sm whitespace-pre-wrap font-outfit">{details.terms}</pre>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Acknowledgment Date</p>
              <p className="text-sm font-medium">{details.acknowledgmentDate ? formatDate(details.acknowledgmentDate) : '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Approval Status</p>
              <Badge className={`text-xs ${getStatusBadgeClass(details.approvalStatus)} border uppercase`}>
                {details.approvalStatus}
              </Badge>
            </div>
          </div>

          {details.attachments?.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium">Attachments</p>
                <div className="flex flex-wrap gap-2">
                  {details.attachments.map((attachment, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs gap-2"
                    >
                      <Paperclip className="h-3.5 w-3.5" />
                      {attachment.name}
                      <Download className="h-3 w-3 text-muted-foreground" />
                    </Button>
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Section 5: Version History */}
      <Card className="border-border/60 shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-sm-secondary" />
            <CardTitle className="text-base font-medium">Version History</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          {/* Current Version (Pinned) - Highlight the latest version from timeline if available */}
          {details.versions.length > 0 && (
            <div className="bg-sm-secondary/5 border border-secondary/20 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge className="bg-sm-secondary text-white text-xs border-transparent hover:bg-sm-secondary/80">Latest</Badge>
                  <span className="text-sm font-medium">v{currentVersion}</span>
                  <span className="text-xs text-muted-foreground">Updated on {formatDate(details.versions[0].updatedOn)}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-semibold text-sm-primary">{formatCurrency(details.versions[0].totalValue)}</span>
                  <span className="text-xs text-muted-foreground">By {details.versions[0].updatedBy}</span>
                </div>
              </div>
            </div>
          )}

          {/* Version History List */}
          {details.versions.slice(1,).map((version) => {
            const isOpen = expandedVersions.includes(version.version);
            return (
              <div
                key={version.version}
                className="border border-border/60 rounded-lg"
              >
                <button
                  onClick={() => toggleVersion(version.version)}
                  className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors rounded-lg bg-transparent border-0"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-xs border-gray-300">v{version.version}</Badge>
                    <span className="text-xs text-muted-foreground">
                      Updated on {formatDate(version.updatedOn)}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium">{formatCurrency(version.totalValue)}</span>
                    <span className="text-xs text-muted-foreground">By {version.updatedBy}</span>
                    {isOpen ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-border/60 p-4 space-y-4 animate-in slide-in-from-top-1 fade-in-0 duration-200">
                    {/* Version Scope Items */}
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground font-medium">Scope Items</p>
                      <div className="rounded-lg border border-border/40 overflow-hidden">
                        <table className="w-full caption-bottom text-sm">
                          <thead className="[&_tr]:border-b">
                            <tr className="border-b transition-colors hover:bg-transparent bg-muted/20">
                              <th className="h-8 px-4 text-left align-middle font-medium text-muted-foreground text-xs">Item</th>
                              <th className="h-8 px-4 text-left align-middle font-medium text-muted-foreground text-xs">Description</th>
                              <th className="h-8 px-4 align-middle font-medium text-muted-foreground text-xs text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody className="[&_tr:last-child]:border-0">
                            {version.scopeItems.map((item) => (
                              <tr key={item.id} className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                                <td className="p-4 align-middle py-2 text-xs">{item.item}</td>
                                <td className="p-4 align-middle py-2 text-xs text-muted-foreground">{item.description}</td>
                                <td className="p-4 align-middle py-2 text-xs text-right">{formatCurrency(item.totalCost)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Version Milestones */}
                    {version.milestones && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground font-medium">Payment Milestones</p>
                        <div className="flex flex-wrap gap-2">
                          {version.milestones.map((m) => (
                            <Badge key={m.id} variant="outline" className="text-xs border-gray-300">
                              {m.name}: {m.percentage.toFixed(1)}%
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Version Terms */}
                    {version.terms && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground font-medium">Terms</p>
                        <p className="text-xs bg-muted/30 p-2 rounded">{version.terms}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {details.versions.length === 0 && (
            <div className="text-center py-6 text-muted-foreground">
              <p className="text-sm">No previous versions available</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
